import { useApolloClient } from "@apollo/client";
import { getUnixTime } from "date-fns/esm";
import { useCallback, useEffect, useState } from "react";

import { makeTimeInRangePredicate } from "utils/predicates";

import { SEARCH_ACCOUNT_RUNS } from "./gql";
import { searchEdgesToEntities } from "./helpers";
import { RunEntity } from "./types";
import { DateRange } from "./Wrapper/types";

const PAGE_SIZE = 100;
// offset paging → parallel bunches, not a serial cursor chain
const CONCURRENCY = 10;

const useAccountRunsSearch = (dateRange: DateRange) => {
  const client = useApolloClient();

  const [entities, setEntities] = useState<Array<RunEntity>>([]);
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState<Error>();
  const [reloadIndex, setReloadIndex] = useState(0);

  const start = getUnixTime(dateRange.startDate);
  const end = getUnixTime(dateRange.endDate);

  // fetch page 0 for the total, fan the rest out across parallel loaders, publish the full set once
  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    // total only on the first page
    const fetchPage = async (offset: number, includeTotal: boolean) => {
      const { data } = await client.query({
        query: SEARCH_ACCOUNT_RUNS,
        fetchPolicy: "no-cache",
        context: { fetchOptions: { signal } },
        variables: {
          includeTotal,
          offset,
          input: {
            after: null,
            first: PAGE_SIZE,
            fullTextSearch: null,
            orderBy: null,
            requestedPage: null,
            predicates: [makeTimeInRangePredicate("createdAt", start, end)],
          },
        },
      });

      return data?.searchRuns;
    };

    const load = async () => {
      setLoading(true);
      setFinished(false);
      setError(undefined);
      setLoaded(0);

      try {
        const firstPage = await fetchPage(0, true);
        if (signal.aborted) return;

        if (!firstPage) {
          setEntities([]);
          setFinished(true);
          return;
        }

        setTotal(firstPage.total);

        // index by page so out-of-order results stay ordered
        const pages: Array<Array<RunEntity>> = [searchEdgesToEntities(firstPage.edges)];
        let loadedCount = pages[0].length;
        setLoaded(loadedCount);

        const pageCount = Math.ceil(firstPage.total / PAGE_SIZE);

        // parallel loaders drain a shared page cursor, CONCURRENCY in flight (page 0 done)
        let next = 1;
        const loader = async () => {
          while (next < pageCount && !signal.aborted) {
            const page = next++;
            const pageData = await fetchPage(page * PAGE_SIZE, false);
            if (signal.aborted || !pageData) return;

            pages[page] = searchEdgesToEntities(pageData.edges);
            loadedCount += pages[page].length;
            setLoaded(loadedCount);
          }
        };

        await Promise.all(Array.from({ length: CONCURRENCY }, loader));

        if (!signal.aborted) {
          // publish once — the graph needs the full set
          setEntities(pages.flat());
          setFinished(true);
        }
      } catch (loadError) {
        if (signal.aborted) return;
        setError(loadError instanceof Error ? loadError : new Error(String(loadError)));
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    load();

    return () => abortController.abort();
  }, [client, start, end, reloadIndex]);

  const refetch = useCallback(() => setReloadIndex((index) => index + 1), []);

  return { entities, loaded, total, loading, finished, error, refetch };
};

export default useAccountRunsSearch;
